import type { FeatureCollection, Point, Position } from "geojson";
import type { GeometryResult } from "@domain/Geo/geometry";
import type { SamplePointCollection, SamplePointProperties, CoreCollection } from "../types";
import { fuzzyMatchSample, detectSampleIdKey } from "./fuzzyValueMatcher";

// ── Property normalization ──────────────────────────────────────────────────

export interface NormalizeOpts {
	/** Explicit property key to extract sample_id from. Bypasses fuzzy matching. */
	sampleIdKey?: string;
}

/**
 * Normalize a raw FeatureCollection (e.g. from uploaded shapefile) into a
 * SamplePointCollection with canonical property names.
 *
 * Resolution chain for sample_id:
 * 1. If opts.sampleIdKey provided → use that key's value
 * 2. Otherwise → fuzzyMatchSample (alias/fuzzy detection)
 * 3. If still empty → auto-generate as String(featureIndex + 1)
 */
export function normalizePointsFC(fc: FeatureCollection, opts?: NormalizeOpts): SamplePointCollection {
	return {
		type: "FeatureCollection",
		features: (fc.features ?? [])
			.filter((f) => f.geometry?.type === "Point")
			.map((f, index) => ({
				type: "Feature" as const,
				geometry: f.geometry as Point,
				properties: normalizeProperties(f.properties, index, opts?.sampleIdKey),
			})),
	};
}

function normalizeProperties(
	raw: Record<string, unknown> | null,
	featureIndex: number,
	sampleIdKey?: string,
): SamplePointProperties {
	const props: Record<string, unknown> = { ...(raw ?? {}) };

	// Resolve sample_id
	let sampleId: string | null = null;
	let resolvedKey: string | null = null;
	if (sampleIdKey && raw && sampleIdKey in raw) {
		sampleId = raw[sampleIdKey] ? String(raw[sampleIdKey]) : null;
		resolvedKey = sampleIdKey;
	} else {
		resolvedKey = detectSampleIdKey(raw);
		sampleId = fuzzyMatchSample(raw);
	}
	props.sample_id = sampleId || String(featureIndex + 1);
	// Clean up the alias key if it differs from canonical
	if (resolvedKey && resolvedKey !== "sample_id" && resolvedKey in props) {
		delete props[resolvedKey];
	}

	// Barcode alias
	if ("spl brcd" in props && !("sample_barcode" in props)) {
		props.sample_barcode = String(props["spl brcd"]);
		delete props["spl brcd"];
	}

	return props as SamplePointProperties;
}

type PartialProps = Partial<Omit<SamplePointProperties, "sample_id">>;

function findIndex(col: SamplePointCollection, sampleId: string): number {
	return col.features.findIndex((f) => f.properties.sample_id === sampleId);
}

function cloneFeatures(col: SamplePointCollection): SamplePointCollection {
	return {
		type: "FeatureCollection",
		features: col.features.map((f) => ({
			...f,
			geometry: { ...f.geometry, coordinates: [...f.geometry.coordinates] },
			properties: { ...f.properties },
		})),
	};
}

export function addPoint(
	col: SamplePointCollection,
	sampleId: string,
	coords: Position,
	extra?: PartialProps,
): GeometryResult<SamplePointCollection> {
	if (findIndex(col, sampleId) !== -1) {
		return { ok: false, error: `Sample "${sampleId}" already exists` };
	}
	const result = cloneFeatures(col);
	result.features.push({
		type: "Feature",
		geometry: { type: "Point", coordinates: [...coords] },
		properties: { sample_id: sampleId, ...extra },
	});
	return { ok: true, data: result };
}

export function updateProperties(
	col: SamplePointCollection,
	sampleId: string,
	props: PartialProps,
): GeometryResult<SamplePointCollection> {
	const idx = findIndex(col, sampleId);
	if (idx === -1) {
		return { ok: false, error: `Sample "${sampleId}" not found` };
	}
	const result = cloneFeatures(col);
	result.features[idx].properties = {
		...result.features[idx].properties,
		...props,
	};
	return { ok: true, data: result };
}

function cloneCoreFeatures(col: CoreCollection): CoreCollection {
	return {
		type: "FeatureCollection",
		features: col.features.map((f) => ({
			...f,
			geometry: { ...f.geometry, coordinates: [...f.geometry.coordinates] },
			properties: { ...f.properties },
		})),
	};
}

export function moveSampleWithCores(
	points: SamplePointCollection,
	cores: CoreCollection,
	sampleId: string,
	newCoords: Position,
): GeometryResult<{ points: SamplePointCollection; cores: CoreCollection }> {
	const idx = findIndex(points, sampleId);
	if (idx === -1) {
		return { ok: false, error: `Sample "${sampleId}" not found` };
	}

	const oldCoords = points.features[idx].geometry.coordinates;
	const delta = [newCoords[0] - oldCoords[0], newCoords[1] - oldCoords[1]];

	const newPoints = cloneFeatures(points);
	newPoints.features[idx].geometry.coordinates = [...newCoords];

	const newCores = cloneCoreFeatures(cores);
	for (const core of newCores.features) {
		if (core.properties.sample_id === sampleId) {
			core.geometry.coordinates = [core.geometry.coordinates[0] + delta[0], core.geometry.coordinates[1] + delta[1]];
		}
	}

	return { ok: true, data: { points: newPoints, cores: newCores } };
}

// ── Index-based ops ───────────────────────────────────────────────────────
// A single job can aggregate points from multiple fields, where per-field
// sample numbering restarts — so `sample_id` is NOT unique within a job. These
// operations identify points by their position in the collection (the same
// unique key the selection/projection layer uses: `points-{jobId}-{index}`),
// which is the only reliable identity for editing.

function indexInRange(col: SamplePointCollection, index: number): boolean {
	return Number.isInteger(index) && index >= 0 && index < col.features.length;
}

export function movePointByIndex(
	col: SamplePointCollection,
	index: number,
	coords: Position,
): GeometryResult<SamplePointCollection> {
	if (!indexInRange(col, index)) {
		return { ok: false, error: `No point at index ${index}` };
	}
	const result = cloneFeatures(col);
	result.features[index].geometry.coordinates = [...coords];
	return { ok: true, data: result };
}

export function deletePointByIndex(col: SamplePointCollection, index: number): GeometryResult<SamplePointCollection> {
	if (!indexInRange(col, index)) {
		return { ok: false, error: `No point at index ${index}` };
	}
	const result = cloneFeatures(col);
	result.features.splice(index, 1);
	return { ok: true, data: result };
}

export function renamePointByIndex(
	col: SamplePointCollection,
	index: number,
	newId: string,
): GeometryResult<SamplePointCollection> {
	if (!indexInRange(col, index)) {
		return { ok: false, error: `No point at index ${index}` };
	}
	// sample_ids must be unique within a job (even one spanning multiple fields):
	// reject a rename that would collide with any other point.
	if (col.features.some((f, i) => i !== index && f.properties.sample_id === newId)) {
		return { ok: false, error: `Sample "${newId}" already exists` };
	}
	const result = cloneFeatures(col);
	result.features[index].properties.sample_id = newId;
	return { ok: true, data: result };
}

export function batchDeletePointsByIndex(
	col: SamplePointCollection,
	indices: number[],
): GeometryResult<SamplePointCollection> {
	for (const idx of indices) {
		if (!indexInRange(col, idx)) {
			return { ok: false, error: `No point at index ${idx}` };
		}
	}
	const idxSet = new Set(indices);
	const result = cloneFeatures(col);
	result.features = result.features.filter((_f, i) => !idxSet.has(i));
	return { ok: true, data: result };
}

export function batchMovePointsByIndex(
	col: SamplePointCollection,
	indices: number[],
	delta: Position,
): GeometryResult<SamplePointCollection> {
	for (const idx of indices) {
		if (!indexInRange(col, idx)) {
			return { ok: false, error: `No point at index ${idx}` };
		}
	}
	const idxSet = new Set(indices);
	const result = cloneFeatures(col);
	result.features.forEach((f, i) => {
		if (idxSet.has(i)) {
			f.geometry.coordinates = [f.geometry.coordinates[0] + delta[0], f.geometry.coordinates[1] + delta[1]];
		}
	});
	return { ok: true, data: result };
}

export interface BatchUpdateEntry {
	sampleId: string;
	properties: PartialProps;
}

export function batchUpdate(
	col: SamplePointCollection,
	updates: BatchUpdateEntry[],
): GeometryResult<SamplePointCollection> {
	// Validate all ids exist first
	for (const u of updates) {
		if (findIndex(col, u.sampleId) === -1) {
			return { ok: false, error: `Sample "${u.sampleId}" not found` };
		}
	}

	const result = cloneFeatures(col);
	for (const u of updates) {
		const idx = result.features.findIndex((f) => f.properties.sample_id === u.sampleId);
		result.features[idx].properties = {
			...result.features[idx].properties,
			...u.properties,
		};
	}
	return { ok: true, data: result };
}
